import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
  Link,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

const PublicLayout: React.FC = () => {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const navItems: { key: string; path: string; label: string }[] = [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#ffffff' }}>
      <AppBar 
        position="fixed"
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: 'none',
          zIndex: 1200,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 }, minHeight: '64px !important' }}>
          <Box
            component="div"
            onClick={() => navigate('/')}
            sx={{ 
              flexGrow: 0, 
              flexShrink: 0,
              mr: { xs: 2, md: 4 },
              cursor: 'pointer',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                letterSpacing: '-0.01em',
                color: '#000000',
              }}
            >
              Ston
            </Typography>
          </Box>
          
          {isSmallScreen ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                color="inherit"
                aria-label="open menu"
                edge="end"
                onClick={handleMobileMenuOpen}
                sx={{
                  color: '#000000',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    mt: 1,
                    minWidth: 240,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <MenuItem
                      key={item.key}
                      selected={isActive}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: isActive ? 500 : 400 }}>
                        {item.label}
                      </Typography>
                    </MenuItem>
                  );
                })}
                <Divider sx={{ my: 1 }} />
                {isAuthenticated ? (
                  <MenuItem
                    onClick={() => handleNavigation('/dashboard')}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <Typography variant="body2">{t('marketing.navigation.dashboard')}</Typography>
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem
                      onClick={() => handleNavigation('/login')}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Typography variant="body2">{t('marketing.navigation.login')}</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation('/register')}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('marketing.navigation.signup')}
                      </Typography>
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Button
                      key={item.key}
                      color="inherit"
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                        color: isActive ? '#000000' : '#666666',
                        fontWeight: isActive ? 500 : 400,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                          color: '#000000',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {isAuthenticated ? (
                  <Button
                    variant="contained"
                    onClick={() => handleNavigation('/dashboard')}
                    sx={{
                      backgroundColor: '#000000',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#333333',
                      },
                    }}
                  >
                    {t('marketing.navigation.dashboard')}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="text"
                      onClick={() => handleNavigation('/login')}
                      sx={{
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {t('marketing.navigation.login')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleNavigation('/register')}
                      sx={{
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#333333',
                        },
                      }}
                    >
                      {t('marketing.navigation.signup')}
                    </Button>
                  </>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          mt: '64px',
          backgroundColor: '#ffffff',
        }}
      >
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          py: 4,
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Ston
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('marketing.footer.tagline')}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('marketing.footer.copyright')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => handleNavigation('/privacy')}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                {t('marketing.footer.privacy')}
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => handleNavigation('/terms')}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                {t('marketing.footer.terms')}
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
